import Skeleton from "../../../components/skeleton/Skeleton";
import { SearchingIndicator } from "./SearchingIndicator";

function SearchingHeader(): React.JSX.Element {
  return (
    <div className="pb-1">
      <SearchingIndicator />
    </div>
  );
}

function repeat(count: number): ReadonlyArray<number> {
  return Array.from({ length: count }, (_, idx) => idx);
}

export function ArticleSkeletonRow(): React.JSX.Element {
  return (
    <div className="flex w-full flex-col gap-2 py-7">
      <Skeleton $variant="rectangular" width="35%" height={10} />
      <div className="flex w-full items-center gap-3">
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton $variant="rectangular" width="95%" height={14} />
          <Skeleton $variant="rectangular" width="70%" height={14} />
        </div>
        <Skeleton $variant="rounded" width={54} height={54} />
      </div>
      <div className="flex w-full items-center justify-between gap-2">
        <Skeleton $variant="rectangular" width="40%" height={10} />
        <Skeleton $variant="rectangular" width="25%" height={10} />
      </div>
    </div>
  );
}

export function ArticlesLoadingSkeleton(): React.JSX.Element {
  return (
    <div className="flex w-full flex-col gap-2">
      <SearchingHeader />
      <ul className="flex w-full flex-col gap-2">
        {repeat(4).map((idx) => (
          <li key={idx} className="w-full">
            <ArticleSkeletonRow />
          </li>
        ))}
      </ul>
    </div>
  );
}

function RoomSkeletonRow(): React.JSX.Element {
  return (
    <div className="flex w-full items-center gap-3 border-b border-ds-black/6 py-3">
      <Skeleton $variant="rounded" width={40} height={40} />
      <div className="flex flex-1 flex-col gap-1.5">
        <Skeleton $variant="rectangular" width="55%" height={12} />
        <Skeleton $variant="rectangular" width="35%" height={10} />
      </div>
    </div>
  );
}

export function RoomsLoadingSkeleton(): React.JSX.Element {
  return (
    <div className="flex w-full flex-col gap-2">
      <SearchingHeader />
      <div className="flex w-full flex-col">
        {repeat(5).map((idx) => (
          <RoomSkeletonRow key={idx} />
        ))}
      </div>
    </div>
  );
}

export function JournalSkeletonRow(): React.JSX.Element {
  return (
    <div className="flex w-full items-center gap-3 rounded-2xl border border-ds-black/6 bg-white p-3">
      <Skeleton $variant="rounded" width={48} height={48} />
      <div className="flex flex-1 flex-col gap-1.5">
        <Skeleton $variant="rectangular" width="70%" height={12} />
        <Skeleton $variant="rectangular" width="40%" height={10} />
      </div>
    </div>
  );
}

export function JournalsLoadingSkeleton(): React.JSX.Element {
  return (
    <div className="flex w-full flex-col gap-2">
      <SearchingHeader />
      <ul className="flex w-full flex-col gap-2">
        {repeat(4).map((idx) => (
          <li key={idx}>
            <JournalSkeletonRow />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PlaylistSkeletonRow(): React.JSX.Element {
  return (
    <div className="flex w-full gap-3 rounded-2xl border border-ds-black/6 bg-white p-3">
      <Skeleton $variant="rounded" width={72} height={72} />
      <div className="flex flex-1 flex-col gap-2 py-1">
        <Skeleton $variant="rectangular" width="70%" height={12} />
        <Skeleton $variant="rectangular" width="50%" height={10} />
        <Skeleton $variant="rectangular" width="30%" height={10} />
      </div>
    </div>
  );
}

export function PlaylistsLoadingSkeleton(): React.JSX.Element {
  return (
    <div className="flex w-full flex-col gap-2">
      <SearchingHeader />
      <ul className="flex w-full flex-col gap-3">
        {repeat(4).map((idx) => (
          <li key={idx}>
            <PlaylistSkeletonRow />
          </li>
        ))}
      </ul>
    </div>
  );
}

function VideoSkeletonRow(): React.JSX.Element {
  return (
    <div className="flex w-full flex-col gap-2">
      <Skeleton $variant="rounded" width="100%" height={180} />
      <Skeleton $variant="rectangular" width="80%" height={12} />
      <Skeleton $variant="rectangular" width="40%" height={10} />
    </div>
  );
}

export function VideosLoadingSkeleton(): React.JSX.Element {
  return (
    <div className="flex w-full flex-col gap-2">
      <SearchingHeader />
      <ul className="flex w-full flex-col gap-3">
        {repeat(3).map((idx) => (
          <li key={idx}>
            <VideoSkeletonRow />
          </li>
        ))}
      </ul>
    </div>
  );
}

function PodcastSkeletonRow(): React.JSX.Element {
  return (
    <div className="flex w-full items-center gap-3 rounded-2xl border border-ds-black/6 bg-white p-3">
      <Skeleton $variant="rounded" width={56} height={56} />
      <div className="flex flex-1 flex-col gap-1.5">
        <Skeleton $variant="rectangular" width="75%" height={12} />
        <Skeleton $variant="rectangular" width="40%" height={10} />
      </div>
    </div>
  );
}

export function PodcastsLoadingSkeleton(): React.JSX.Element {
  return (
    <div className="flex w-full flex-col gap-2">
      <SearchingHeader />
      <ul className="flex w-full flex-col gap-2">
        {repeat(4).map((idx) => (
          <li key={idx}>
            <PodcastSkeletonRow />
          </li>
        ))}
      </ul>
    </div>
  );
}

function TopRoomSkeletonCard(): React.JSX.Element {
  return (
    <div className="flex w-full items-center gap-3 rounded-2xl border border-ds-black/6 bg-white p-4">
      <Skeleton $variant="rounded" width={56} height={56} />
      <div className="flex flex-1 flex-col gap-2">
        <Skeleton $variant="rectangular" width="60%" height={14} />
        <Skeleton $variant="rectangular" width="40%" height={10} />
      </div>
      <Skeleton $variant="rounded" width={92} height={32} />
    </div>
  );
}

export function AllLoadingSkeleton({
  withTopRoom,
}: {
  withTopRoom: boolean;
}): React.JSX.Element {
  return (
    <div className="flex w-full flex-col gap-4">
      {withTopRoom && <TopRoomSkeletonCard />}
      <div className="flex w-full flex-col gap-2">
        <SearchingHeader />
        <ul className="flex w-full flex-col gap-2">
          {repeat(4).map((idx) => (
            <li key={idx} className="w-full">
              <ArticleSkeletonRow />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
